import React, { useState } from 'react';
import { Tool } from './types';
import MergeTool from './components/MergeTool';
import SplitTool from './components/SplitTool';
import ExtractTextTool from './components/ExtractTextTool';
import ResumeTool from './components/ResumeTool';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import PdfToDocxTool from './components/PdfToDocxTool';
import DocxToPdfTool from './components/DocxToPdfTool';
import PptxToPdfTool from './components/PptxToPdfTool';
import ImageToPdfTool from './components/ImageToPdfTool';
import CompressTool from './components/CompressTool';
import ProtectTool from './components/ProtectTool';
import WatermarkTool from './components/WatermarkTool';
import ExtractImagesTool from './components/ExtractImagesTool';
import { MergeIconCard, SplitIconCard, ExtractIconCard, ResumeIconCard, PdfToDocxIconCard, ImageToPdfIconCard, CompressIconCard, ProtectIconCard, WatermarkIconCard, DocxToPdfIconCard, PptxToPdfIconCard, ExtractImagesIconCard } from './components/icons';
import { useTranslation } from './i18n';

const App: React.FC = () => {
  const [activeTool, setActiveTool] = useState<Tool | null>(null);
  const [view, setView] = useState<'main' | 'terms' | 'privacy'>('main');
  const { t, locale, setLocale } = useTranslation();

  const tools = [
    // Manipulation
    // FIX: Cast results of `t` function to string to satisfy ToolCard prop types.
    { id: Tool.Merge, title: t('toolMergeTitle') as string, description: t('toolMergeDescription') as string, icon: <MergeIconCard /> },
    { id: Tool.Split, title: t('toolSplitTitle') as string, description: t('toolSplitDescription') as string, icon: <SplitIconCard /> },
    { id: Tool.Compress, title: t('toolCompressTitle') as string, description: t('toolCompressDescription') as string, icon: <CompressIconCard /> },
    // Security & Stamping
    { id: Tool.Protect, title: t('toolProtectTitle') as string, description: t('toolProtectDescription') as string, icon: <ProtectIconCard /> },
    { id: Tool.Watermark, title: t('toolWatermarkTitle') as string, description: t('toolWatermarkDescription') as string, icon: <WatermarkIconCard /> },
    // Conversion
    { id: Tool.PdfToDocx, title: t('toolPdfToDocxTitle') as string, description: t('toolPdfToDocxDescription') as string, icon: <PdfToDocxIconCard /> },
    { id: Tool.DocxToPdf, title: t('toolDocxToPdfTitle') as string, description: t('toolDocxToPdfDescription') as string, icon: <DocxToPdfIconCard /> },
    { id: Tool.PptxToPdf, title: t('toolPptxToPdfTitle') as string, description: t('toolPptxToPdfDescription') as string, icon: <PptxToPdfIconCard /> },
    { id: Tool.ImageToPdf, title: t('toolImageToPdfTitle') as string, description: t('toolImageToPdfDescription') as string, icon: <ImageToPdfIconCard /> },
    // Content Extraction & AI
    { id: Tool.ExtractText, title: t('toolExtractTitle') as string, description: t('toolExtractDescription') as string, icon: <ExtractIconCard /> },
    { id: Tool.ExtractImages, title: t('toolExtractImagesTitle') as string, description: t('toolExtractImagesDescription') as string, icon: <ExtractImagesIconCard /> },
    { id: Tool.Resume, title: t('toolResumeTitle') as string, description: t('toolResumeDescription') as string, icon: <ResumeIconCard /> },
  ];

  const renderTool = () => {
    switch (activeTool) {
      case Tool.Merge:
        return <MergeTool />;
      case Tool.Split:
        return <SplitTool />;
      case Tool.Compress:
        return <CompressTool />;
      case Tool.Protect:
        return <ProtectTool />;
      case Tool.Watermark:
        return <WatermarkTool />;
      case Tool.PdfToDocx:
        return <PdfToDocxTool />;
      case Tool.DocxToPdf:
        return <DocxToPdfTool />;
      case Tool.PptxToPdf:
        return <PptxToPdfTool />;
      case Tool.ImageToPdf:
        return <ImageToPdfTool />;
      case Tool.ExtractText:
        return <ExtractTextTool />;
      case Tool.ExtractImages:
        return <ExtractImagesTool />;
      case Tool.Resume:
        return <ResumeTool />;
      default:
        return null;
    }
  };
  
  const LanguageSelector: React.FC = () => {
    const languages: { code: 'en' | 'pt' | 'es'; name: string }[] = [
      { code: 'en', name: 'English' },
      { code: 'pt', name: 'Português' },
      { code: 'es', name: 'Español' },
    ];

    return (
      <div className="flex justify-center items-center gap-2">
        {languages.map(({ code, name }) => (
          <button
            key={code}
            onClick={() => setLocale(code)}
            className={`px-3 py-1 text-xs rounded-full transition-colors ${
              locale === code
                ? 'bg-indigo-600 text-white font-semibold'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-400'
            }`}
          >
            {name}
          </button>
        ))}
      </div>
    );
  };

  const ToolCard: React.FC<{ tool: Tool; title: string; description: string; icon: React.ReactElement }> = ({ tool, title, description, icon }) => (
    <button
      onClick={() => setActiveTool(tool)}
      className="bg-slate-800/50 hover:bg-slate-800 border border-slate-700 rounded-2xl p-6 text-left flex flex-col items-center justify-center text-center transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl hover:shadow-indigo-500/10"
    >
      {icon}
      <h3 className="font-bold text-slate-200 text-lg mt-2">{title}</h3>
      <p className="text-sm text-slate-400 mt-1">{description}</p>
    </button>
  );

  const renderMainContent = () => {
    switch (view) {
      case 'terms':
        return <TermsOfUse />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'main':
      default:
        if (activeTool !== null) {
          return (
            <>
              <nav className="mb-8 -mx-2 animate-fade-in">
                  <div className="flex items-center space-x-2 overflow-x-auto pb-2 px-2">
                    <button
                      onClick={() => setActiveTool(null)}
                      className="flex-shrink-0 text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-2 transition-colors pr-2"
                      aria-label={t('ariaBackToTools')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      {t('allTools')}
                    </button>
                    <div className="border-l border-slate-700 h-6"></div>
                    {tools.map(({ id, title }) => (
                      <button
                        key={id}
                        onClick={() => setActiveTool(id)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                          activeTool === id
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        {title}
                      </button>
                    ))}
                  </div>
                </nav>
              <main>{renderTool()}</main>
            </>
          );
        }

        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
            {tools.map(tool => (
              <ToolCard key={tool.id} tool={tool.id} title={tool.title} description={tool.description} icon={tool.icon} />
            ))}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 font-sans p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto flex flex-col min-h-screen">
        <header className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-200">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-500">DS</span> DOC Tools
          </h1>
          <p className="mt-2 text-slate-400">{t('appSubtitle')}</p>
        </header>
        
        <div className="flex-grow">
          {view !== 'main' && (
            <div className="mb-8">
              <button 
                onClick={() => { setView('main'); setActiveTool(null); }}
                className="text-indigo-400 hover:text-indigo-300 font-semibold flex items-center gap-2 transition-colors"
                aria-label={t('ariaBackToTools')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {t('backToTools')}
              </button>
            </div>
          )}
          {renderMainContent()}
        </div>

        <footer className="text-center mt-12 py-6 border-t border-slate-800">
          <div className="flex justify-center items-center gap-6 mb-4">
            <button onClick={() => setView('terms')} className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium">
              {t('termsOfUse')}
            </button>
            <button onClick={() => setView('privacy')} className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium">
              {t('privacyPolicy')}
            </button>
          </div>
          <div className="mb-4">
            <LanguageSelector />
          </div>
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} DS Logic. {t('footerRights')}</p>
        </footer>
      </div>
    </div>
  );
};

export default App;